// Grid View Component - Cell Cards Grid
// Simple wrapper for the cells grid, logic handled in MainAppPage

import { useState, useEffect, useRef } from 'preact/hooks'
import { displayMode, sortBy, sortOrder, basis, domain, showGridImages, showGridBadges } from '../../state/globalSignals.js'
import { isInCompare, addToCompare, removeFromCompare, resetAllFilters } from '../../state/filterStore.js'
import {
  isDemoCatalogUnlocked,
  isCellLockedAtLimit,
  openLimitModalOrToast,
} from '../../state/demoLimitStore.js'
import { consumeCompareDeepDive, unlockCell } from '../../services/demoLimitService.js'
import { isChemistryLockedForDemo } from '../../utils/metricTiering.js'
import { addedDateSortValue, compareNullsLast, qualitySortValue } from '../../utils/cellSort.js'
import { IconGrid, IconPlus, IconCheck, IconBattery, IconZap } from '../shared/Icons.jsx'
import { showToast } from '../shared/Toast.jsx'

const ITEMS_PER_PAGE = 30 // 30 cells per page

// Grid images intermittently fail to load (network jitter, browser connection
// limits, a momentarily slow DB/Redis round-trip on the signed-picture
// endpoint) — a single onError must not blank the thumbnail for the card's
// whole lifetime. Retry a few times with backoff before falling back to the
// placeholder icon.
const MAX_IMG_RETRIES = 2 // up to 3 load attempts total

/**
 * Get basis-aware value and unit
 */
function getBasisValue(cell, field, currentBasis, currentDomain) {
  const isDomain = (d) => currentDomain === d
  
  // Normalized data: electrical.* fields are already in UI units (Wh, Ah, W, etc.)
  if (field === 'energy') {
    if (currentBasis === 'abs') {
      return { value: cell.electrical?.E_c10_Wh ? cell.electrical.E_c10_Wh.toFixed(1) : '—', unit: 'Wh' }
    } else if (currentBasis === 'grav') {
      return { value: cell.electrical?.E_grav_Wh_per_kg ? cell.electrical.E_grav_Wh_per_kg.toFixed(0) : '—', unit: 'Wh/kg' }
    } else if (currentBasis === 'vol') {
      return { value: cell.electrical?.E_vol_Wh_per_L ? cell.electrical.E_vol_Wh_per_L.toFixed(0) : '—', unit: 'Wh/L' }
    }
  } else if (field === 'power') {
    if (currentBasis === 'abs') {
      return { value: cell.electrical?.P_peak_W ? cell.electrical.P_peak_W.toFixed(0) : '—', unit: 'W' }
    } else if (currentBasis === 'grav') {
      return { value: cell.electrical?.P_grav_W_per_kg ? cell.electrical.P_grav_W_per_kg.toFixed(0) : '—', unit: 'W/kg' }
    } else if (currentBasis === 'vol') {
      return { value: cell.electrical?.P_vol_W_per_L ? cell.electrical.P_vol_W_per_L.toFixed(0) : '—', unit: 'W/L' }
    }
  } else if (field === 'capacity') {
    if (currentBasis === 'abs') {
      return { value: cell.electrical?.C_c10_Ah ? cell.electrical.C_c10_Ah.toFixed(2) : '—', unit: 'Ah' }
    } else if (currentBasis === 'grav') {
      return { value: cell.electrical?.C_grav_Ah_per_kg ? cell.electrical.C_grav_Ah_per_kg.toFixed(1) : '—', unit: 'Ah/kg' }
    } else if (currentBasis === 'vol') {
      return { value: cell.electrical?.C_vol_Ah_per_L ? cell.electrical.C_vol_Ah_per_L.toFixed(0) : '—', unit: 'Ah/L' }
    }
  } else if (field === 'current') {
    if (currentBasis === 'abs') {
      return { value: cell.electrical?.I_peak_A ? cell.electrical.I_peak_A.toFixed(1) : '—', unit: 'A' }
    } else if (currentBasis === 'grav') {
      return { value: cell.electrical?.I_grav_A_per_kg ? cell.electrical.I_grav_A_per_kg.toFixed(0) : '—', unit: 'A/kg' }
    } else if (currentBasis === 'vol') {
      return { value: cell.electrical?.I_vol_A_per_L ? cell.electrical.I_vol_A_per_L.toFixed(0) : '—', unit: 'A/L' }
    }
  } else if (field === 'voltage') {
    return { value: cell.electrical?.V_mean ? cell.electrical.V_mean.toFixed(2) : '—', unit: 'V' }
  } else if (field === 'mass') {
    return { value: cell.mechanical?.m_cell_g ? cell.mechanical.m_cell_g.toFixed(0) : '—', unit: 'g' }
  }
  
  return { value: '—', unit: '' }
}

/**
 * Cell Card with lazy-loaded image using Intersection Observer
 * Uses signed thumbnail_url from explorer API (no additional auth requests)
 */
function CellCard({ cell, index, displayMode, onCellClick, onLockedCellClick }) {
  const [isVisible, setIsVisible] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const cardRef = useRef(null)
  const retryTimeoutRef = useRef(null)
  // DEMO users get the full catalog unlocked (subject to the daily view quota),
  // so shadow-lock only genuine free/expired tiers — even if a stale 24h-cached
  // page still carries is_locked=true.
  const isLocked = cell.is_locked && !isDemoCatalogUnlocked()
  // DEMO storefront (2026-07-20): a demo cell not yet unlocked today shows a
  // server-side BLURRED thumbnail (backend gates the sharp bytes) with a lock
  // overlay, so you still see a frosted preview of every catalogue cell and
  // know it's locked. Unlocking it flips is_locked=false → sharp image + no
  // overlay on the next explorer load. Mutually exclusive with `isLocked`
  // (that branch is the non-demo free/expired shadow-lock).
  const demoLocked = cell.is_locked && isDemoCatalogUnlocked()
  // Bug B: DEMO users at today's deep-dive limit see a lock affordance on
  // cells they haven't opened yet (already-opened cells stay free — see
  // isCellLockedAtLimit). Distinct from the shadow-lock above (which only
  // applies to non-DEMO free/expired tiers) — the two are mutually exclusive.
  const atLimitLocked = isCellLockedAtLimit(cell.id)
  const inCompare = !isLocked && isInCompare(cell.id)
  const currentBasis = basis.value
  const currentDomain = domain.value
  // Governing-rule soft friction (Decision 1A): demo users see the chemistry
  // badge as a lock until the cell is unlocked (in-place or via a same-day
  // detail view). Clicking it spends the same Deep-Dive token as opening the
  // cell — see handleChemistryUnlock below. Never true for non-demo tiers.
  const chemistryLocked = isChemistryLockedForDemo(cell)

  // Click the locked chemistry badge → unlock this cell in place via the
  // Deep-Dive token (not the PRO-upsell path, which stays reserved for the
  // genuine free/expired shadow-lock above). unlockCell handles a 429 (at
  // today's limit) itself by opening the conversion modal; any other error
  // is caught here so a transient failure never crashes the card.
  const handleChemistryUnlock = async (e) => {
    e.stopPropagation() // don't also trigger the card's own onClick
    try {
      await unlockCell({ productId: cell.id, surface: 'grid' })
    } catch (err) {
      // 429 (DEMO_LIMIT) is handled inside unlockCell itself (opens the
      // conversion modal) and never reaches here. Any other failure gets a
      // visible toast instead of a silent console.warn.
      showToast("Couldn't unlock this cell. Please try again.")
    }
  }

  // Intersection Observer for true lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect() // Stop observing once visible
          }
        })
      },
      {
        // Kept tight (was 100px): a 30-card page revealed almost every card
        // at once on load, firing 20-30 simultaneous thumbnail requests at
        // the same origin — a big contributor to the timing-dependent image
        // failures this fix addresses. 0px only loads cards actually inside
        // (or touching) the viewport.
        rootMargin: '0px',
        threshold: 0.01
      }
    )

    if (cardRef.current) {
      observer.observe(cardRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Clean up any pending retry timeout on unmount so we never setState after
  // the card is gone.
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
    }
  }, [])

  // Use thumbnail_url from explorer API (already signed). The signature only
  // covers product_id:pic_name:expires (see verify_picture_signature in
  // backend/app/api/v1/products/router.py) — an extra query param is not
  // part of the signed message and does not break verification, so it's
  // safe to use as a cache-busting retry marker.
  const thumbnailUrl = cell.thumbnail_url
  const retrySrc =
    retryCount > 0 && thumbnailUrl
      ? `${thumbnailUrl}${thumbnailUrl.includes('?') ? '&' : '?'}r=${retryCount}`
      : thumbnailUrl

  const handleImageError = () => {
    if (retryCount < MAX_IMG_RETRIES) {
      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null
        setRetryCount((c) => c + 1)
      }, 300 * (retryCount + 1))
    } else {
      setImageError(true)
    }
  }
  
  // Get domain-specific metrics
  const isDomainEP = currentDomain === 'energy-power'
  const metric1 = getBasisValue(cell, isDomainEP ? 'energy' : 'capacity', currentBasis, currentDomain)
  const metric2 = getBasisValue(cell, isDomainEP ? 'power' : 'current', currentBasis, currentDomain)
  const metric3 = getBasisValue(cell, 'voltage', currentBasis, currentDomain)
  const metric4 = getBasisValue(cell, 'mass', currentBasis, currentDomain) // For spacious mode
  
  return (
    <div
      ref={cardRef}
      className={`factsheet-card factsheet-card--${displayMode} ${inCompare ? 'in-compare' : ''} ${isLocked ? 'factsheet-card--locked' : ''} ${atLimitLocked ? 'factsheet-card--limit-locked' : ''}`}
      onClick={(e) => {
        if (atLimitLocked) {
          openLimitModalOrToast({})
          return
        }
        if (isLocked) {
          if (onLockedCellClick) onLockedCellClick(e.currentTarget, cell)
        } else if (onCellClick) {
          onCellClick(e.currentTarget, cell)
        }
      }}
      data-cell-id={cell.id}
      {...(index === 0 ? { 'data-tour': 'explorer-cell-card' } : {})}
    >
      {/* Compare Button — hidden for locked cells and (Bug 3B) for a demo user
          at today's deep-dive limit on a not-yet-opened cell, so they can't add
          an over-the-limit cell that would 429 on the compare-view load. Cells
          already opened today (atLimitLocked=false) stay addable (idempotent). */}
      {!isLocked && !atLimitLocked && (
        <button
          className={`factsheet-card__compare ${inCompare ? 'active' : ''}`}
          onClick={async (e) => {
            e.stopPropagation()
            // Removing never counts a deep-dive.
            if (inCompare) {
              removeFromCompare(cell.id)
              return
            }
            // Adding: eager deep-dive gate (counts live for demo; blocks at limit).
            const ok = await consumeCompareDeepDive({
              id: cell.id,
              name: cell.name,
              manufacturer: cell.manufacturer,
            })
            if (ok) addToCompare(cell.id)
          }}
          title={inCompare ? 'Remove from comparison' : 'Add to comparison'}
          {...(index === 0 ? { 'data-tour': 'explorer-compare-btn' } : {})}
        >
          {inCompare ? <IconCheck size={14} /> : <IconPlus size={14} />}
        </button>
      )}

      {/* Cell Image - uses signed URL directly, loaded via Intersection Observer */}
      {showGridImages.value && displayMode !== 'compact' && (
        <div className="factsheet-card__image">
          {/* Lock overlay for locked cells */}
          {isLocked && (
            <div className="factsheet-card__lock-overlay">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
          )}
          {/* Lock overlay for cells not yet opened at today's deep-dive limit */}
          {atLimitLocked && (
            <div
              className="factsheet-card__lock-overlay"
              aria-label="Deep-dive limit reached — resets soon"
              title="Deep-dive limit reached — resets soon"
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
          )}
          {/* Lock overlay over the blurred preview of a not-yet-unlocked demo
              cell (distinct from atLimitLocked, which wins when at the limit). */}
          {demoLocked && !atLimitLocked && (
            <div
              className="factsheet-card__lock-overlay"
              aria-label="Locked — unlock to see the full image and identity"
              title="Locked — unlock to see the full image and identity"
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
          )}
          {isVisible && thumbnailUrl && !imageError ? (
            <img
              src={retrySrc}
              alt={cell.name || cell.id}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              onError={handleImageError}
              style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.2s' }}
            />
          ) : null}
          {(!isVisible || !thumbnailUrl || imageError || !imageLoaded) && (
            <div className="factsheet-card__image-placeholder" style={{ display: imageLoaded ? 'none' : 'flex' }}>
              <IconBattery size={32} />
            </div>
          )}
        </div>
      )}
      
      {/* Cell Header */}
      <div className="factsheet-card__header">
        <h3 className="factsheet-card__title">{cell.name || cell.id}</h3>
        <p className="factsheet-card__subtitle">{cell.manufacturer || 'Unknown'}</p>
      </div>
      
      {/* Spec Rows - Domain & Basis Aware */}
      <div className="factsheet-card__specs">
        <div className="spec-row">
          <span className="spec-label">{isDomainEP ? 'Energy' : 'Capacity'}</span>
          <span className="spec-value">
            <span className="spec-number">{metric1.value}</span>
            <span className="spec-unit">{metric1.unit}</span>
          </span>
        </div>
        
        <div className="spec-row">
          <span className="spec-label">{isDomainEP ? 'Power' : 'Current'}</span>
          <span className="spec-value">
            <span className="spec-number">{metric2.value}</span>
            <span className="spec-unit">{metric2.unit}</span>
          </span>
        </div>
        
        {displayMode !== 'compact' && !isLocked && (
          <div className="spec-row">
            <span className="spec-label">Voltage</span>
            <span className="spec-value">
              <span className="spec-number">{metric3.value}</span>
              <span className="spec-unit">{metric3.unit}</span>
            </span>
          </div>
        )}
        
        {displayMode === 'spacious' && (
          <div className="spec-row">
            <span className="spec-label">Mass</span>
            <span className="spec-value">
              <span className="spec-number">{metric4.value}</span>
              <span className="spec-unit">{metric4.unit}</span>
            </span>
          </div>
        )}
      </div>

      {/* Cell Footer */}
      {showGridBadges.value && (
        <div className="factsheet-card__footer">
          {!isLocked && (
            chemistryLocked ? (
              <span
                className="locked-metric locked-metric--gated factsheet-badge--chemistry-locked"
                role="button"
                tabIndex={0}
                title="Unlock this cell to see its chemistry — costs 1 deep dive"
                aria-label="Chemistry locked — unlock this cell, 1 deep dive"
                onClick={handleChemistryUnlock}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleChemistryUnlock(e)
                  }
                }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                {/* isChemistryLockedForDemo (this branch) is only ever true for
                    demo users — a genuine PRO-upsell chemistry lock does not
                    exist on Grid, so the label is unconditionally "Unlock". */}
                <span className="locked-metric__pro">Unlock</span>
                {/* Visible cost hint (Task A2 follow-up) — title/aria alone
                    is invisible on touch and needs a hover on desktop. */}
                <span className="locked-metric__cost">· 1 deep dive</span>
              </span>
            ) : (
              <span className="factsheet-badge factsheet-badge--chemistry">
                {cell.chemical?.main_chemistry || cell.chemical?.cathode || 'N/A'}
              </span>
            )
          )}
          <span className="factsheet-badge factsheet-badge--format">
            {cell.mechanical?.format_group || cell.mechanical?.format || 'N/A'}
          </span>
        </div>
      )}
    </div>
  )
}

export default function GridView({ cells, loading, onCellClick, onLockedCellClick }) {
  const currentDisplayMode = displayMode.value
  const currentSortBy = sortBy.value
  const currentSortOrder = sortOrder.value
  const [currentPage, setCurrentPage] = useState(1)
  
  // Ensure cells is an array
  const cellsArray = Array.isArray(cells) ? cells : []
  const currentBasis = basis.value
  
  // Apply sorting: demo cells first, then locked cells; user sort within each group
  const sortedCells = [...cellsArray].sort((a, b) => {
    // Primary sort: demo cells (is_locked=false) before locked cells
    if (a.is_locked !== b.is_locked) return a.is_locked ? 1 : -1

    let aVal, bVal
    let result = 0

    switch (currentSortBy) {
      case 'name':
        aVal = (a.name || '').toLowerCase()
        bVal = (b.name || '').toLowerCase()
        result = aVal < bVal ? -1 : (aVal > bVal ? 1 : 0)
        break
      case 'manufacturer':
        aVal = (a.manufacturer || '').toLowerCase()
        bVal = (b.manufacturer || '').toLowerCase()
        result = aVal < bVal ? -1 : (aVal > bVal ? 1 : 0)
        break
      case 'chemistry':
        aVal = (a.chemical?.main_chemistry || a.chemical?.cathode || '').toLowerCase()
        bVal = (b.chemical?.main_chemistry || b.chemical?.cathode || '').toLowerCase()
        result = aVal < bVal ? -1 : (aVal > bVal ? 1 : 0)
        break
      case 'energy':
        if (currentBasis === 'abs') {
          aVal = parseFloat(a.electrical?.E_c10_Wh || 0)
          bVal = parseFloat(b.electrical?.E_c10_Wh || 0)
        } else if (currentBasis === 'grav') {
          aVal = parseFloat(a.electrical?.E_grav_Wh_per_kg || 0)
          bVal = parseFloat(b.electrical?.E_grav_Wh_per_kg || 0)
        } else if (currentBasis === 'vol') {
          aVal = parseFloat(a.electrical?.E_vol_Wh_per_L || 0)
          bVal = parseFloat(b.electrical?.E_vol_Wh_per_L || 0)
        }
        result = aVal < bVal ? -1 : (aVal > bVal ? 1 : 0)
        break
      case 'power':
        if (currentBasis === 'abs') {
          aVal = parseFloat(a.electrical?.P_peak_W || 0)
          bVal = parseFloat(b.electrical?.P_peak_W || 0)
        } else if (currentBasis === 'grav') {
          aVal = parseFloat(a.electrical?.P_grav_W_per_kg || 0)
          bVal = parseFloat(b.electrical?.P_grav_W_per_kg || 0)
        } else if (currentBasis === 'vol') {
          aVal = parseFloat(a.electrical?.P_vol_W_per_L || 0)
          bVal = parseFloat(b.electrical?.P_vol_W_per_L || 0)
        }
        result = aVal < bVal ? -1 : (aVal > bVal ? 1 : 0)
        break
      case 'capacity':
        if (currentBasis === 'abs') {
          aVal = parseFloat(a.electrical?.C_c10_Ah || 0)
          bVal = parseFloat(b.electrical?.C_c10_Ah || 0)
        } else if (currentBasis === 'grav') {
          aVal = parseFloat(a.electrical?.C_grav_Ah_per_kg || 0)
          bVal = parseFloat(b.electrical?.C_grav_Ah_per_kg || 0)
        } else if (currentBasis === 'vol') {
          aVal = parseFloat(a.electrical?.C_vol_Ah_per_L || 0)
          bVal = parseFloat(b.electrical?.C_vol_Ah_per_L || 0)
        }
        result = aVal < bVal ? -1 : (aVal > bVal ? 1 : 0)
        break
      case 'year':
        // The MANUFACTURER'S RELEASE year — not when Batemo added the cell
        // (that is 'addedDate' below).
        aVal = a.year || 1900
        bVal = b.year || 1900
        result = aVal < bVal ? -1 : (aVal > bVal ? 1 : 0)
        break
      case 'aging':
      case 'batchVariation':
        // Returns EARLY with the order already applied: these two need nulls
        // last in both directions, which the shared `-result` flip below would
        // undo. See utils/cellSort.js.
        return compareNullsLast(
          qualitySortValue(a, currentSortBy),
          qualitySortValue(b, currentSortBy),
          currentSortOrder,
        )
      case 'addedDate':
        // Catalogue-INSERTION date. Returns EARLY (already order-applied) so
        // cells with no insertion date stay last in both directions instead of
        // being flipped to the front by the `asc ? result : -result` below.
        return compareNullsLast(
          addedDateSortValue(a),
          addedDateSortValue(b),
          currentSortOrder
        )
      default:
        return 0
    }
    
    // Apply sort order
    return currentSortOrder === 'asc' ? result : -result
  })
  
  // Pagination on sorted data
  const totalPages = Math.ceil(sortedCells.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedCells = sortedCells.slice(startIndex, endIndex)
  
  // Reset to page 1 when cells change
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1)
  }
  
  // Only show loading spinner during initial load (when cells array is completely empty)
  // After initial load, show "No Cells Found" for empty filtered results
  if (loading && cellsArray.length === 0) {
    return (
      <div className="loading-spinner">
        <div className="loading-spinner__animation">
          <div className="loading-spinner__ring"></div>
          <div className="loading-spinner__core"></div>
          <div className="loading-spinner__icon">
            <IconGrid size={28} />
          </div>
        </div>
        <div className="loading-spinner__text">Loading Cells</div>
        <div className="loading-spinner__subtext">Preparing cell grid...</div>
      </div>
    )
  }
  
  if (!sortedCells || sortedCells.length === 0) {
    return (
      <div className="empty-state-modern">
        <div className="empty-state-card">
          <div className="empty-state-icon-wrapper">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="7" height="7" rx="1"></rect>
              <rect x="14" y="3" width="7" height="7" rx="1"></rect>
              <rect x="3" y="14" width="7" height="7" rx="1"></rect>
              <rect x="14" y="14" width="7" height="7" rx="1"></rect>
            </svg>
          </div>
          <h3 className="empty-state-title">No Cells Found</h3>
          <p className="empty-state-description">
            No battery cells match your current filters.
            <br />
            Try adjusting or clearing some filters to see more results.
          </p>
          <button 
            className="empty-state-action"
            onClick={() => resetAllFilters()}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18"></path>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Clear All Filters
          </button>
        </div>
      </div>
    )
  }
  
  return (
    <div className="cells-grid-view">
      <div className={`cells-grid cells-grid--${currentDisplayMode}`}>
        {paginatedCells.map((cell, index) => (
          <CellCard
            key={cell.id}
            cell={cell}
            index={index}
            displayMode={currentDisplayMode}
            onCellClick={onCellClick}
            onLockedCellClick={onLockedCellClick}
          />
        ))}
      </div>
      
      {/* Pagination Controls */}
      {totalPages >= 1 && (
        <div className="pagination-controls">
          <button
            className="pagination-btn pagination-btn--first"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            title="First page"
          >
            ««
          </button>
          
          <button
            className="pagination-btn pagination-btn--prev"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            title="Previous page"
          >
            ‹ Prev
          </button>
          
          <div className="pagination-info">
            <span className="pagination-current">Page {currentPage}</span>
            <span className="pagination-divider">/</span>
            <span className="pagination-total">{totalPages}</span>
            <span className="pagination-count">
              ({startIndex + 1}–{Math.min(endIndex, sortedCells.length)} of {sortedCells.length})
            </span>
          </div>
          
          <button
            className="pagination-btn pagination-btn--next"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            title="Next page"
          >
            Next ›
          </button>
          
          <button
            className="pagination-btn pagination-btn--last"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            title="Last page"
          >
            »»
          </button>
        </div>
      )}
    </div>
  )
}
